import React, {useMemo} from 'react';

import withSSRPage from 'coreComponents/withSSRPage';
import {useClientServerEffect, useConfiguration, useLocalesMgr, useLocation, usePageInit, useServerData} from 'coreHooks';
import {absoluteUrl} from 'coreUtils/url';

import {MAX_CACHE_AGE} from 'brand/app/constants';
import {useGeneralProvider} from 'brandComponents/_app-config/GeneralProvider';

import {StoreLocatorProvider} from './context/StoreLocatorContext';
import StoreLocatorContainer from './Subcomponents/StoreLocatorContainer';

const StoreLocatorContent = () => {
	const {managerRepository} = useGeneralProvider();
	const location = useLocation();
	const sitePreferences = useConfiguration('sitePreferences');
	const storeLocatorGoogleMapsAPIKey = sitePreferences.storeLocatorGoogleMapsAPIKey;
	const localesMgr = useLocalesMgr();

	const result = useServerData(
		'store-locator-data',
		async () => {
			const slug = managerRepository.getLocalesMgr().getUrlWithoutLocale(location.pathname);
			const metadata = [];

			const cmsProvider = managerRepository.getContentMgr().getCMSProvider();
			const data = await cmsProvider.getContent('page', {variables: {slug}}, true);
			const pageMetadata = data?.pageCollection?.items?.[0]?.metadata;

			if (pageMetadata) {
				metadata.push(pageMetadata);
			}

			const canonicalUrl = location.search ? `${location.pathname}${location.search}` : location.pathname;

			return {canonicalUrl, metadata};
		},
		[location.pathname, location.search],
	);

	const slug = useMemo(() => {
		return localesMgr.getUrlWithoutLocale(location.pathname);
	}, [location.pathname]);

	usePageInit({
		route: 'StoreLocator',
		cache: MAX_CACHE_AGE,
		seo: {
			metadata: result.metadata,
			pageUrls: slug,
			jsonld: [],
			canonical: absoluteUrl(result.canonicalUrl),
		},
	});

	// Not in the queryFn: it does not run again when the page data is cached.
	useClientServerEffect(() => {
		managerRepository.getUiMgr().addHtmlCssClasses(['stores']);

		return () => {
			managerRepository.getUiMgr().removeHtmlCssClasses(['stores']);
		};
	}, []);

	return (
		<StoreLocatorProvider>
			<StoreLocatorContainer mapApiKey={storeLocatorGoogleMapsAPIKey} />
		</StoreLocatorProvider>
	);
};

export default withSSRPage(StoreLocatorContent, 'store-locator');
